import * as React from 'react';
import { ReactNode, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ArrowDown,
  ArrowLeftSmall,
  ArrowRightSmall,
  Calendar,
  Select,
  TimePicker,
} from '@ankr.com/ui';
import {
  Box,
  Button,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DayOfTheWeekRange } from 'cron-parser/types';
import cronstrue from 'cronstrue';
import { add, format, isFuture } from 'date-fns';
import { makeStyles } from 'tss-react/mui';

import { useFormatDayOfWeek } from 'modules/common';
import { useTranslation } from 'modules/i18n';

import {
  IntervalType,
  useIntervalOptions,
} from '../../hooks/useIntervalOptions';
import { useWeekDaysOptions } from '../../hooks/useWeekDaysOptions';
import { addDayOfWeekToExpression } from '../../utils/addDayOfWeekToExpression';
import { getTodayDifferenceInSeconds } from '../../utils/getTodayDifferenceInSeconds';
import { isValidCronExpression } from '../../utils/isValidCronExpression';
import { Scheduler } from '../Scheduler';
import { Checkbox } from './Checkbox';
import translation from './whenForm.translation';

export interface WhenFormSubmitParams {
  cronExpression: string;
  startDate: Date | null;
  startImmediately: boolean;
}

export interface WhenFormValues {
  cronWeekDays: DayOfTheWeekRange[];
  cronExpression: string;
  intervalType: IntervalType;
  intervalExpression: string;
  startImmediately: boolean;
  startDate: Date;
  time: number;
  isCustomExpression: boolean;
}

interface Props {
  isCompatible: boolean;
  onSubmit: (state: WhenFormSubmitParams) => void;
  initialFormValues: WhenFormValues | null;
  onSaveFormValues: (formState: WhenFormValues) => void;
}

const INITIAL_TIME_DATE = new Date(`${new Date().getFullYear()}/01/01`);
const INITIAL_CRON_EXPRESSION = '* * * * *';
const MIN_DATE = new Date();
const TIME_STEP = 30 * 60; // 30 minutes step in seconds
const DAY_SECONDS = 24 * 60 * 60; // 1 day in seconds

export function WhenForm({
  isCompatible,
  onSubmit,
  initialFormValues,
  onSaveFormValues,
}: Props): JSX.Element {
  const { t, keys } = useTranslation(translation);
  const daysOptions = useWeekDaysOptions();
  const { classes } = useStyles();
  const formatDayOfWeek = useFormatDayOfWeek();

  const initialTime = useMemo(() => {
    const diff = getTodayDifferenceInSeconds();
    const time = Math.round(diff / TIME_STEP) * TIME_STEP;
    if (time > DAY_SECONDS) {
      return DAY_SECONDS;
    }

    return time;
  }, []);

  const {
    control,
    watch,
    setValue,
    resetField,
    handleSubmit: handleSubmitForm,
  } = useForm<WhenFormValues>({
    mode: 'onChange',
    defaultValues: initialFormValues ?? {
      cronWeekDays: [],
      cronExpression: INITIAL_CRON_EXPRESSION,
      intervalType: IntervalType.Hours,
      startDate: new Date(),
      startImmediately: true,
      time: initialTime,
      isCustomExpression: false,
    },
  });

  const cronWeekDays = watch('cronWeekDays');
  const intervalExpression = watch('intervalExpression');
  const cronExpression = watch('cronExpression');
  const startImmediately = watch('startImmediately');
  const startDate = watch('startDate');
  const timeValue = watch('time');
  const isCustomExpression = watch('isCustomExpression');
  const intervalType = watch('intervalType');

  const { intervalTypeOptions, intervalValuesOptions } =
    useIntervalOptions(intervalType);

  const handleFormatTime = useCallback(
    (seconds: number) =>
      format(
        add(INITIAL_TIME_DATE, {
          seconds,
        }),
        'hh:mm a',
      ),
    [],
  );

  const cronExpressionPreview = useMemo(() => {
    if (isCustomExpression) {
      return cronExpression;
    }

    const isInitialSchedule =
      !cronWeekDays?.length &&
      (typeof intervalExpression !== 'string' || !intervalExpression);

    if (isInitialSchedule) {
      return '';
    }

    return addDayOfWeekToExpression(
      intervalExpression || INITIAL_CRON_EXPRESSION,
      cronWeekDays,
    );
  }, [isCustomExpression, cronExpression, cronWeekDays, intervalExpression]);

  const handleChangeSwitcher = (_: unknown, checked: boolean): void => {
    if (checked) {
      resetField('cronWeekDays');
      resetField('intervalExpression');
      setValue('isCustomExpression', true);
      setValue('cronExpression', cronExpressionPreview, {
        shouldValidate: true,
      });
    } else {
      setValue('cronExpression', INITIAL_CRON_EXPRESSION);
      setValue('isCustomExpression', false);
    }
  };

  const validateCronExpression = (expression: string): boolean | string => {
    return isValidCronExpression(expression) || t(keys.invalidCronExpression);
  };

  const renderDaysSelectValue = (value: unknown): ReactNode => {
    const days = value as number[];
    if (!days.length) {
      return (
        <Box component="span" color={theme => theme.palette.grey[600]}>
          {t(keys.daysPlaceholder)}
        </Box>
      );
    }

    return days.length === 1
      ? daysOptions.find(option => option.id === days[0])?.name
      : t(keys.daysAmount, { amount: days.length });
  };

  const handleSubmit = handleSubmitForm(formValues => {
    const { startDate, time, startImmediately } = formValues;
    if (!startDate) {
      return;
    }

    const fullStartDate = startImmediately
      ? new Date()
      : add(new Date(format(startDate, 'yyyy/MM/dd')), {
          seconds: time,
        });

    void onSubmit({
      startImmediately,
      cronExpression: cronExpressionPreview,
      startDate: fullStartDate,
    });
    void onSaveFormValues(formValues);
  });

  const isSubmitDisabled = useMemo(() => {
    if (isCustomExpression) {
      return !isValidCronExpression(cronExpressionPreview);
    }

    return !intervalExpression;
  }, [cronExpressionPreview, intervalExpression, isCustomExpression]);

  if (isCompatible) {
    return (
      <Box display="flex" flexDirection="column">
        <Typography variant="subtitle2" component="div">
          {t(keys.conditionInfo)}
        </Typography>

        <Typography variant="body2" mt={8} component="div">
          {t(keys.conditionDescription)}
        </Typography>

        <Button
          data-tid="create-task-when-condition-submit-button"
          size="large"
          type="button"
          sx={{ mt: 10 }}
          onClick={() =>
            onSubmit({
              startImmediately,
              cronExpression: '',
              startDate: null,
            })
          }
        >
          {t(keys.submit)}
        </Button>
      </Box>
    );
  }

  return (
    <form className={classes.container} onSubmit={handleSubmit}>
      <div className={classes.titleWithSwitcher}>
        <Typography variant="subtitle3">{t(keys.executeContract)}</Typography>

        <div className={classes.switch}>
          <Typography variant="subtitle3">{t(keys.cronExpression)}</Typography>

          <Switch
            data-tid="create-task-when-custom-expression-switch"
            value={isCustomExpression}
            onChange={handleChangeSwitcher}
          />
        </div>
      </div>

      {isCustomExpression ? (
        <Box mt={8} mb={4}>
          <Typography
            data-tid="create-task-when-cron-expression"
            variant="button1"
            textAlign="center"
            paragraph
          >
            {isValidCronExpression(cronExpression)
              ? cronstrue.toString(cronExpression)
              : '?'}
          </Typography>

          <Controller
            control={control}
            name="cronExpression"
            rules={{ validate: validateCronExpression }}
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <TextField
                data-tid="create-task-when-cron-expression-input"
                variant="filled"
                label={t(keys.cronExpression)}
                value={value ?? ''}
                onChange={onChange}
                error={!!errors.cronExpression}
                helperText={errors.cronExpression?.message}
                fullWidth
              />
            )}
          />
        </Box>
      ) : (
        <div className={classes.selects}>
          <Controller
            control={control}
            name="cronWeekDays"
            render={({ field: { value = [], onChange } }) => {
              return (
                <Select
                  data-tid="create-task-when-cron-week-days-select"
                  label={t(keys.dayLabel)}
                  value={value}
                  multiple
                  variant="filled"
                  className={classes.select}
                  inputProps={{ placeholder: t(keys.daysPlaceholder) }}
                  onChange={onChange}
                  renderValue={renderDaysSelectValue}
                  displayEmpty
                >
                  {daysOptions.map(({ id, name }) => (
                    <MenuItem
                      key={id}
                      value={id}
                      data-tid={`create-task-when-cron-week-days-select-item-${id}`}
                    >
                      <Checkbox checked={value.includes(id)} />

                      <Typography variant="body2" ml={2}>
                        {name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />

          <Controller
            control={control}
            name="intervalType"
            render={({ field: { value = '', onChange } }) => {
              return (
                <Select
                  data-tid="create-task-when-interval-type-select"
                  label={t(keys.intervalTypeLabel)}
                  value={value}
                  variant="filled"
                  className={classes.select}
                  inputProps={{ placeholder: t(keys.intervalTypePlaceholder) }}
                  onChange={event => {
                    resetField('intervalExpression');
                    onChange(event);
                  }}
                  displayEmpty
                  renderValue={value =>
                    value ? (
                      intervalTypeOptions.find(({ id }) => id === value)?.name
                    ) : (
                      <Box
                        component="span"
                        color={theme => theme.palette.grey[600]}
                      >
                        {t(keys.intervalTypePlaceholder)}
                      </Box>
                    )
                  }
                >
                  {intervalTypeOptions.map(({ id, name }) => (
                    <MenuItem
                      key={id}
                      value={id}
                      data-tid={`create-task-when-interval-type-select-item-${id}`}
                    >
                      <Typography variant="body2" ml={2}>
                        {name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />

          <Controller
            control={control}
            name="intervalExpression"
            render={({ field: { value = '', onChange } }) => {
              return (
                <Select
                  data-tid="create-task-when-interval-select"
                  label={t(keys.intervalValueLabel)}
                  value={value}
                  variant="filled"
                  className={classes.select}
                  inputProps={{ placeholder: t(keys.intervalValuePlaceholder) }}
                  onChange={onChange}
                  displayEmpty
                  renderValue={value =>
                    value ? (
                      intervalValuesOptions.find(({ id }) => id === value)?.name
                    ) : (
                      <Box
                        component="span"
                        color={theme => theme.palette.grey[600]}
                      >
                        {t(keys.intervalValuePlaceholder)}
                      </Box>
                    )
                  }
                >
                  {intervalValuesOptions.map(({ id, name }) => (
                    <MenuItem
                      key={id}
                      value={id}
                      data-tid={`create-task-when-interval-select-item-${id}`}
                    >
                      <Typography variant="body2" ml={2}>
                        {name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />
        </div>
      )}

      <Box display="flex" alignItems="flex-end" gap={4} mt={4}>
        <Controller
          name="startDate"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <MuiDatePicker
              data-tid="create-task-when-start-date-picker"
              disabled={startImmediately}
              value={value}
              onChange={date => {
                if (date && !isFuture(date)) {
                  const diffInSeconds = getTodayDifferenceInSeconds();
                  if (Math.abs(diffInSeconds) > timeValue) {
                    setValue('time', initialTime);
                  }
                }

                onChange(date);
              }}
              minDate={MIN_DATE}
              inputFormat="dd MMM yyyy"
              views={['year', 'month', 'day']}
              components={{
                OpenPickerIcon: Calendar,
                RightArrowIcon: ArrowRightSmall,
                LeftArrowIcon: ArrowLeftSmall,
                SwitchViewButton: ArrowDown,
              }}
              dayOfWeekFormatter={formatDayOfWeek}
              renderInput={params => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { onChange: onChangeInput, ...inputProps } =
                  params.inputProps ?? {};
                return (
                  <TextField
                    data-tid="create-task-when-start-date-picker-input"
                    sx={{
                      flex: 1,
                    }}
                    variant="filled"
                    {...params}
                    inputProps={inputProps}
                    label={t(keys.startTime)}
                  />
                );
              }}
            />
          )}
        />

        <Controller
          name="time"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <TimePicker
              data-tid="create-task-when-time-picker"
              disabled={startImmediately}
              sx={{ flex: 1 }}
              variant="filled"
              value={value}
              onFormat={handleFormatTime}
              onChange={seconds => {
                if (!isFuture(startDate)) {
                  const diffInSeconds = getTodayDifferenceInSeconds();
                  if (Math.abs(diffInSeconds) > seconds) {
                    return;
                  }
                }

                onChange(seconds);
              }}
            />
          )}
        />
      </Box>

      <div className={classes.checkBoxPanel}>
        <Controller
          name="startImmediately"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              data-tid="create-task-when-start-immediately-checkbox"
              checked={value}
              onChange={onChange}
            />
          )}
        />

        <Typography variant="button2" ml={2.5}>
          {t(keys.startImmediately)}
        </Typography>
      </div>

      <Typography variant="subtitle2" mb={8} mt={8}>
        {t(keys.executionSchedule)}
      </Typography>

      <Scheduler cronExpression={cronExpressionPreview} />

      <Button
        data-tid="create-task-when-submit-button"
        size="large"
        type="submit"
        sx={{ mt: 10 }}
        disabled={isSubmitDisabled}
      >
        {t(keys.submit)}
      </Button>
    </form>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleWithSwitcher: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  checkBoxPanel: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(4),
  },
  selects: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
    marginTop: theme.spacing(3),

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'initial',
    },
  },
  select: {
    flex: 1,
  },
  switch: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
}));
